import React, { useCallback, useMemo } from "react";
import FloorCreateIntro from "./floor-create-intro";
import { FloorCreateDetails } from "./floor-create-details";
import FloorCreateImportPrompt from "./floor-create-import-prompt";
import FloorCreateImportUpload from "./floor-create-import-upload";
import { FloorCreateImportReview } from "./floor-create-import-review";
import FloorCreatePlaceImage from "./floor-create-place-image";
import FloorCreateDrawPrompt from "./floor-create-draw-prompt";
import FloorCreateDrawControls from "./floor-create-draw-controls";
import { FloorCreateStep } from "../../../../state/floor/floor-create-step";
import {
	DRAW_REFERENCE_FLOOR_ID_CURRENT,
	FloorCreateActions,
	FloorCreateState,
} from "../../../../state/floor/floor-create-state";
import ReferenceFloor from "../../../../state/floor/reference-floor";

export interface FloorCreateProps {
	onClose?: () => void;
	onFinish?: () => void;

	disableNext?: boolean;

	state: FloorCreateState;
	actions: FloorCreateActions;
}

const FLOOR_SELECT_OPTION_PENDING = "pending";

export default function FloorCreate(props: FloorCreateProps) {
	const step = props.state.step;
	const setStep = props.actions.setStep;
	const onFinish = props.onFinish;

	const floorImage = props.state.floorImage;

	const siteFloors = useMemo(() => {
		return props.state.activeSite?.floors ?? [];
	}, [props.state.activeSite?.floors]);

	const drawReferenceFloors = useMemo((): ReferenceFloor[] => {
		const referenceFloors: ReferenceFloor[] = [];

		//If we have uploaded an image, add it as an option at the top of the list
		if (floorImage !== null) {
			referenceFloors.push({
				id: FLOOR_SELECT_OPTION_PENDING,
				level: props.state.metadata.level,
				name: props.state.metadata.name,
				current: true,
			});
		}

		//Add all existing floors as options
		if (props.state.activeSite?.floors) {
			referenceFloors.push(
				...props.state.activeSite.floors
					.filter((floor) => floor.img !== undefined)
					.map((floor) => ({
						id: floor.id,
						name: floor.name,
						level: floor.level,
						current: false,
					})),
			);
		}

		return referenceFloors;
	}, [
		props.state.activeSite,
		props.state.metadata.level,
		props.state.metadata.name,
		floorImage,
	]);
	const drawReferenceFloorID = useMemo(() => {
		if (
			props.state.drawReferenceFloorID === DRAW_REFERENCE_FLOOR_ID_CURRENT
		) {
			return FLOOR_SELECT_OPTION_PENDING;
		} else {
			return props.state.drawReferenceFloorID;
		}
	}, [props.state.drawReferenceFloorID]);

	const propsSetDrawReferenceFloorID = props.actions.setDrawReferenceFloorID;
	const setDrawReferenceFloor = useCallback(
		(floorID: string | null) => {
			if (floorID === FLOOR_SELECT_OPTION_PENDING) {
				propsSetDrawReferenceFloorID(DRAW_REFERENCE_FLOOR_ID_CURRENT);
			} else {
				propsSetDrawReferenceFloorID(floorID);
			}
		},
		[propsSetDrawReferenceFloorID],
	);

	switch (step) {
		case FloorCreateStep.Intro:
			return (
				<FloorCreateIntro
					onClose={props.onClose}
					onNext={() => setStep(FloorCreateStep.Details)}
				/>
			);
		case FloorCreateStep.Details:
			return (
				<FloorCreateDetails
					existingFloors={siteFloors}
					onClose={props.onClose}
					onSubmit={(floorNum: number, floorName: string) => {
						props.actions.setMetadata({
							level: floorNum,
							name: floorName,
						});
						setStep(FloorCreateStep.ImportPrompt);
					}}
				/>
			);
		case FloorCreateStep.ImportPrompt:
			return (
				<FloorCreateImportPrompt
					onClose={props.onClose}
					onSelectUpload={() => setStep(FloorCreateStep.ImportUpload)}
					onSelectSkip={() => setStep(FloorCreateStep.Draw)}
				/>
			);
		case FloorCreateStep.ImportUpload:
			return (
				<FloorCreateImportUpload
					onClose={props.onClose}
					onBack={() => setStep(FloorCreateStep.ImportPrompt)}
					onUpload={(floorImage) => {
						props.actions.setFloorImage(floorImage);
						setStep(FloorCreateStep.ImportReview);
					}}
				/>
			);
		case FloorCreateStep.ImportReview:
			return (
				<FloorCreateImportReview
					image={floorImage!}
					onClose={props.onClose}
					onBack={() => {
						props.actions.setFloorImage(null);
						setStep(FloorCreateStep.ImportUpload);
					}}
					onNext={() => {
						setStep(FloorCreateStep.Place);
					}}
				/>
			);
		case FloorCreateStep.Place:
			return (
				<FloorCreatePlaceImage
					onClose={props.onClose}
					onConfirm={() => {
						setStep(FloorCreateStep.DrawPrompt);
					}}
					availableFloors={siteFloors}
					imageOpacity={props.state.floorImageOpacity}
					onChangeImageOpacity={(opacity) => {
						props.actions.setFloorImageOpacity(opacity);
					}}
					referenceFloorID={props.state.placeImageReferenceFloorID}
					onSelectReferenceFloor={
						props.actions.setPlaceImageReferenceFloorID
					}
				/>
			);
		case FloorCreateStep.DrawPrompt:
			return (
				<FloorCreateDrawPrompt
					onClose={props.onClose}
					onNext={(selection) => {
						if (selection === "image") {
							onFinish?.();
						} else if (selection === "draw") {
							setStep(FloorCreateStep.Draw);

							//If we uploaded a floorplan, select it as the reference floor by default
							if (props.state.floorImage !== null) {
								props.actions.setDrawReferenceFloorID(
									DRAW_REFERENCE_FLOOR_ID_CURRENT,
								);
							}
						}
					}}
					disableNext={props.disableNext ?? false}
				/>
			);
		case FloorCreateStep.Draw:
			return (
				<FloorCreateDrawControls
					drawTool={props.state.drawToolType}
					onChangeDrawTool={props.actions.setDrawToolType}
					onClose={props.onClose}
					onFinish={onFinish}
					disableFinish={props.disableNext ?? false}
					floors={drawReferenceFloors}
					referenceFloorID={drawReferenceFloorID}
					onSelectReferenceFloor={setDrawReferenceFloor}
				/>
			);
	}
}
